import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
	Box,
	CircularProgress,
	Typography,
	TextField,
	Button,
} from "@mui/material";
import { FiInbox } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parseISO, isWithinInterval, addDays } from "date-fns";
import { Download } from "lucide-react"; // Optional icon

const UploadedFilesTable = () => {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchText, setSearchText] = useState("");
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);

	const navigate = useNavigate();

	useEffect(() => {
		const fetchData = async () => {
			try {
				const token = localStorage.getItem("token");
				const res = await axios.get("https://parallex.approot.ng/upload.php", {
					headers: { Authorization: `Bearer ${token}` },
				});

				if (res.data.status && Array.isArray(res.data.data)) {
					const formatted = res.data.data.map((file) => {
						const totalCount = Number(file.total_count) || 0;
						const totalDistinct = Number(file.total_distinct) || 0;
						const count = Number(file.count) || 0;

						let statusText = "Unknown";
						if (file.status === "4") statusText = "Uploaded";
						else if (file.status === "0" || file.status === "1")
							statusText = "Processing";
						else if (file.status === "2") statusText = "Completed";
						else if (file.status === "3" || file.status === "5")
							statusText = "Failed";

						return {
							id: file.id,
							...file,
							total_count: totalCount,
							total_distinct: totalDistinct,
							status: statusText,
							count: count > 0 ? count : 1,
							duplicates: Math.max(0, totalCount - totalDistinct),
						};
					});

					setRows(formatted);
				} else {
					setRows([]);
				}
			} catch (error) {
				console.error("Failed to fetch file data:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	const filteredRows = rows.filter((row) => {
		const nameMatch = row.file_name
			?.toLowerCase()
			.includes(searchText.toLowerCase());

		if (!startDate && !endDate) return nameMatch;

		const rowDate = row.created_at ? parseISO(row.created_at) : null;
		if (!rowDate) return false;

		const inRange =
			startDate && endDate
				? isWithinInterval(rowDate, {
						start: startDate,
						end: addDays(endDate, 1),
				  })
				: startDate
				? rowDate >= startDate
				: rowDate <= addDays(endDate, 1);

		return nameMatch && inRange;
	});

	const handleRowClick = (params) => {
		const fileDetails = params.row;
		if (fileDetails.status !== "Processing") {
			navigate(`/dashboard/file-summary/${fileDetails.batch_id}`, {
				state: { file: fileDetails },
			});
		}
	};

	// ✅ Manual Export Function
	const handleExportCSV = () => {
		if (filteredRows.length === 0) {
			alert("No data to export");
			return;
		}

		const headers = Object.keys(filteredRows[0]);
		const csvContent = [
			headers.join(","),
			...filteredRows.map((row) =>
				headers
					.map((field) => `"${String(row[field] ?? "").replace(/"/g, '""')}"`)
					.join(",")
			),
		].join("\n");

		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute(
			"download",
			`upload-history-${new Date().toISOString().split("T")[0]}.csv`
		);
		link.click();
	};

	const columns = [
		{ field: "id", headerName: "ID", width: 70 },
		{ field: "file_name", headerName: "File Name", width: 200 },
		{ field: "full_name", headerName: "Uploader", width: 150 },
		{ field: "batch_id", headerName: "Batch ID", width: 170 },
		{ field: "type", headerName: "Type", width: 100 },
		{ field: "msg_cat", headerName: "Category", width: 120 },
		{
			field: "total_count",
			headerName: "Total Count",
			width: 120,
			type: "number",
		},
		{
			field: "total_distinct",
			headerName: "Total Distinct",
			width: 130,
			type: "number",
		},
		{
			field: "duplicates",
			headerName: "Duplicates",
			width: 100,
			type: "number",
		},
		{ field: "count", headerName: "Amount Sent", width: 120, type: "number" },
		{
			field: "status",
			headerName: "Status",
			width: 120,
			renderCell: (params) => {
				const colorClass =
					params.value === "Completed"
						? "text-green-600 font-semibold"
						: params.value === "Processing"
						? "text-yellow-600 font-semibold"
						: params.value === "Failed"
						? "text-red-600 font-semibold"
						: params.value === "Uploaded"
						? "text-blue-600 font-semibold"
						: "";
				return <span className={colorClass}>{params.value}</span>;
			},
		},
		{ field: "cost_cntr", headerName: "Cost Center", width: 180 },
		{ field: "created_at", headerName: "Created At", width: 180 },
		{ field: "message", headerName: "Message", width: 250 },
		{ field: "schedule_time", headerName: "Scheduled At", width: 180 },
		{ field: "uploaded_by", headerName: "Uploaded By", width: 180 },
	];

	return (
		<Box sx={{ height: 600, width: "100%", padding: 2 }}>
			<Box className='flex items-center justify-between mb-4'>
				<Typography variant='h5'>Sent Message History</Typography>
				<Button
					variant='contained'
					color='primary'
					onClick={handleExportCSV}
					startIcon={<Download size={18} />}
				>
					Export CSV
				</Button>
			</Box>

			<TextField
				label='Search by File Name'
				variant='outlined'
				size='small'
				fullWidth
				margin='normal'
				value={searchText}
				onChange={(e) => setSearchText(e.target.value)}
			/>

			<LocalizationProvider dateAdapter={AdapterDateFns}>
				<Box
					sx={{
						display: "flex",
						gap: 2,
						mb: 2,
						flexWrap: "wrap",
						maxWidth: 520,
					}}
				>
					<DatePicker
						label='Start Date'
						value={startDate}
						onChange={(newValue) => setStartDate(newValue)}
						slotProps={{ textField: { size: "small", fullWidth: true } }}
					/>
					<DatePicker
						label='End Date'
						value={endDate}
						onChange={(newValue) => setEndDate(newValue)}
						slotProps={{ textField: { size: "small", fullWidth: true } }}
					/>
				</Box>
			</LocalizationProvider>

			{loading ? (
				<Box className='flex items-center justify-center mt-16'>
					<CircularProgress />
				</Box>
			) : filteredRows.length === 0 ? (
				<Box className='text-center text-gray-500 mt-20'>
					<FiInbox className='text-6xl mx-auto mb-2' />
					<Typography variant='subtitle1'>No matching files found</Typography>
				</Box>
			) : (
				<DataGrid
					rows={filteredRows}
					columns={columns}
					pageSizeOptions={[10, 20, 50]}
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
						sorting: { sortModel: [{ field: "created_at", sort: "desc" }] },
					}}
					onRowClick={handleRowClick}
					sx={{
						height: "100%",
						cursor: "pointer",
						"& .MuiDataGrid-row.Mui-hovered": { backgroundColor: "#f5f5f5" },
						"& .MuiDataGrid-row.bg-yellow-50": { cursor: "not-allowed" },
					}}
				/>
			)}
		</Box>
	);
};

export default UploadedFilesTable;
